import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { fileUrl } from '../fileUrl';

const ATTACHMENT_ACCEPT = '.jpg,.jpeg,.png,.pdf,.xls,.xlsx,image/jpeg,image/png,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const ALLOWED_ATTACHMENT_EXTS = ['.jpg', '.jpeg', '.png', '.pdf', '.xls', '.xlsx'];
const ALLOWED_ATTACHMENT_MIMES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const isAllowedAttachment = (file) => {
  if (!file) return false;
  const name = (file.name || '').toLowerCase();
  const hasAllowedExt = ALLOWED_ATTACHMENT_EXTS.some(ext => name.endsWith(ext));
  const hasAllowedMime = ALLOWED_ATTACHMENT_MIMES.includes(file.type);
  return hasAllowedExt && (hasAllowedMime || !file.type);
};

const getClipboardAttachment = (clipboardData) => {
  const files = Array.from(clipboardData?.files || []);
  const directFile = files.find(isAllowedAttachment);
  if (directFile) return directFile;

  const items = Array.from(clipboardData?.items || []);
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (isAllowedAttachment(file)) return file;
  }

  return null;
};

const handleAttachmentPaste = (event, onFile) => {
  const file = getClipboardAttachment(event.clipboardData);
  if (!file) return;
  event.preventDefault();
  onFile(file);
};

/* ── Statusy ─────────────────────────────────────────────────────────────── */

const STATUS_CFG = {
  DO_ZROBIENIA: { bg: '#1c2b3a', color: '#58a6ff', border: '#1f4068' },
  W_TRAKCIE:    { bg: '#7c3500', color: '#ff8c00', border: '#a04800' },
  ZROBIONE:     { bg: '#0d3320', color: '#3fb950', border: '#1a5c38' },
};

function Badge({ value }) {
  const c = STATUS_CFG[value] || { bg: '#1f1f1f', color: '#8b949e', border: '#30363d' };
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: '4px', padding: '2px 8px', fontSize: '12px',
      fontWeight: '600', whiteSpace: 'nowrap', display: 'inline-block',
    }}>
      {value?.replace('_', ' ') || '—'}
    </span>
  );
}

function StatusDropdown({ value, isAdmin, onChange }) {
  const [open, setOpen] = useState(false);
  const [hov, setHov]   = useState(null);
  const [pos, setPos]   = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  if (!isAdmin) return <Badge value={value} />;

  const handleOpen = (e) => {
    e.stopPropagation();
    if (!open) {
      const rect = e.currentTarget.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(o => !o);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <span onClick={handleOpen} style={{ cursor: 'pointer', display: 'inline-block' }}>
        <Badge value={value} />
      </span>
      {open && (
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, background: '#161b22', border: '1px solid #30363d', borderRadius: '6px', padding: '4px 0', minWidth: '150px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
          {Object.keys(STATUS_CFG).map(opt => (
            <div
              key={opt}
              onClick={e => { e.stopPropagation(); onChange(opt); setOpen(false); }}
              onMouseEnter={() => setHov(opt)}
              onMouseLeave={() => setHov(null)}
              style={{ padding: '6px 12px', cursor: 'pointer', background: hov === opt ? '#21262d' : 'transparent' }}
            >
              <Badge value={opt} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Drawer ──────────────────────────────────────────────────────────────── */

function TaskDrawer({ taskId, isAdmin, user, onClose, onUpdated }) {
  const [task, setTask]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [lightbox, setLightbox]     = useState(null);

  // notatki / załączniki
  const [newNote, setNewNote]           = useState('');
  const [savingNote, setSavingNote]     = useState(false);
  const [attachFile, setAttachFile]     = useState(null);
  const [attachPreview, setAttachPreview] = useState('');
  const fileInputRef = useRef(null);
  const [editNoteId, setEditNoteId]     = useState(null);
  const [editNoteText, setEditNoteText] = useState('');

  // komentarze
  const [newComment, setNewComment]         = useState('');
  const [savingComment, setSavingComment]   = useState(false);
  const [commentFile, setCommentFile]       = useState(null);
  const [commentPreview, setCommentPreview] = useState('');
  const commentFileRef = useRef(null);
  const [editCommentId, setEditCommentId]     = useState(null);
  const [editCommentText, setEditCommentText] = useState('');

  // pozycje
  const [itemModal, setItemModal] = useState(null);
  const [itemForm, setItemForm]   = useState({ kod_modelu: '', ilosc: '', uwagi: '' });
  const [savingItem, setSavingItem] = useState(false);
  const [itemError, setItemError]   = useState('');
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const kodModeluRef = useRef(null);

  // edycja zadania
  const [editOpen, setEditOpen]   = useState(false);
  const [editForm, setEditForm]   = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError]   = useState('');
  const [factories, setFactories]   = useState([]);
  const [deleteConfirmTask, setDeleteConfirmTask] = useState(false);
  const [confirmAllDoneDrawer, setConfirmAllDoneDrawer] = useState(false);

  const canEdit = isAdmin || (task && task.created_by === user.id);

  const load = async () => {
    try {
      const res = await api.get(`/tasks/${taskId}`);
      setTask(res.data);
    } catch { setError('Błąd pobierania zadania'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [taskId]); // eslint-disable-line

  const refresh = async () => {
    const res = await api.get(`/tasks/${taskId}`);
    setTask(res.data);
    onUpdated && onUpdated(res.data);
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('pl-PL') : '—';

  const handleToggleItemDrawer = async (item) => {
    const newVal = !item.zrobione;
    try {
      await api.put(`/task-items/${item.id}`, { zrobione: newVal });
      const updated = await api.get(`/tasks/${taskId}`);
      setTask(updated.data);
      onUpdated && onUpdated(updated.data);
      if (newVal && updated.data.items.every(it => it.zrobione) && (isAdmin || updated.data.assigned_to === user.id)) {
        setConfirmAllDoneDrawer(true);
      }
    } catch {}
  };

  const handleConfirmTaskDoneDrawer = async () => {
    await api.put(`/tasks/${taskId}`, { status: 'ZROBIONE' });
    setConfirmAllDoneDrawer(false);
    await refresh();
  };

  /* ── Edycja zadania ── */
  const [users, setUsers] = useState([]);

  const openEdit = () => {
    setEditForm({ nazwa: task.nazwa, fabryka_id: task.fabryka_id || '', assigned_to: task.assigned_to || '' });
    if (factories.length === 0) api.get('/factories').then(r => setFactories(r.data)).catch(() => {});
    if (users.length === 0) api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    setEditError('');
    setEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.put(`/tasks/${taskId}`, {
        nazwa:       editForm.nazwa,
        fabryka_id:  editForm.fabryka_id  || null,
        assigned_to: editForm.assigned_to || null,
      });
      setEditOpen(false);
      await refresh();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Błąd zapisu');
    } finally { setSavingEdit(false); }
  };

  /* ── Usuń zadanie ── */
  const handleDeleteTask = async () => {
    try {
      await api.delete(`/tasks/${taskId}`);
      setDeleteConfirmTask(false);
      onClose();
      onUpdated && onUpdated(null);
    } catch {}
  };

  /* ── Status ── */
  const handleStatusChange = async (status) => {
    await api.put(`/tasks/${taskId}`, { status });
    await refresh();
  };

  /* ── Notatki / załączniki ── */
  const handleAttachFile = (file) => {
    if (!file) return;
    if (!isAllowedAttachment(file)) {
      alert('Dozwolone tylko pliki JPG, PNG, PDF, XLS i XLSX');
      return;
    }
    setAttachFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => setAttachPreview(e.target.result);
      reader.readAsDataURL(file);
    } else { setAttachPreview(''); }
  };

  const clearAttach = () => {
    setAttachFile(null);
    setAttachPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addNoteOrPhoto = async (e) => {
    e.preventDefault();
    if (!newNote.trim() && !attachFile) return;
    setSavingNote(true);
    try {
      if (attachFile) {
        const fd = new FormData();
        fd.append('plik', attachFile);
        fd.append('opis', newNote.trim());
        fd.append('typ', 'ZADANIE');
        fd.append('obiekt_id', taskId);
        await api.post('/photos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/notes', { tresc: newNote.trim(), typ: 'ZADANIE', obiekt_id: taskId });
      }
      setNewNote('');
      clearAttach();
      await refresh();
    } catch {} finally { setSavingNote(false); }
  };

  const deleteNote  = async (id) => { try { await api.delete(`/notes/${id}`);  await refresh(); } catch {} };
  const deletePhoto = async (id) => { try { await api.delete(`/photos/${id}`); await refresh(); } catch {} };

  const startEditNote = (n) => { setEditNoteId(n.id); setEditNoteText(n.tresc); };
  const saveEditNote  = async (id) => {
    try { await api.put(`/notes/${id}`, { tresc: editNoteText }); setEditNoteId(null); await refresh(); } catch {}
  };

  /* ── Komentarze ── */
  const handleCommentFile = (file) => {
    if (!file) return;
    if (!isAllowedAttachment(file)) {
      alert('Dozwolone tylko pliki JPG, PNG, PDF, XLS i XLSX');
      return;
    }
    setCommentFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = ev => setCommentPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else { setCommentPreview(''); }
  };

  const clearCommentFile = () => {
    setCommentFile(null);
    setCommentPreview('');
    if (commentFileRef.current) commentFileRef.current.value = '';
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() && !commentFile) return;
    setSavingComment(true);
    try {
      const fd = new FormData();
      fd.append('tresc', newComment.trim());
      fd.append('typ', 'ZADANIE');
      fd.append('obiekt_id', taskId);
      if (commentFile) fd.append('zdjecie', commentFile);
      await api.post('/comments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewComment('');
      clearCommentFile();
      await refresh();
    } catch {} finally { setSavingComment(false); }
  };

  const deleteComment = async (id) => { try { await api.delete(`/comments/${id}`); await refresh(); } catch {} };
  const startEditComment = (c) => { setEditCommentId(c.id); setEditCommentText(c.tresc); };
  const saveEditComment  = async (id) => {
    try { await api.put(`/comments/${id}`, { tresc: editCommentText }); setEditCommentId(null); await refresh(); } catch {}
  };

  /* ── Pozycje ── */
  const openAddItem = () => {
    setItemForm({ kod_modelu: '', ilosc: '', uwagi: '' });
    setItemError('');
    setItemModal({ mode: 'add' });
  };
  const openEditItem = (item) => {
    setItemForm({ kod_modelu: item.kod_modelu || '', ilosc: String(item.ilosc ?? ''), uwagi: item.uwagi || '' });
    setItemError('');
    setItemModal({ mode: 'edit', item });
  };
  const closeItemModal = () => { setItemModal(null); setItemError(''); };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setSavingItem(true);
    setItemError('');
    const payload = {
      task_id:    taskId,
      kod_modelu: itemForm.kod_modelu,
      ilosc:      itemForm.ilosc !== '' ? parseInt(itemForm.ilosc) : null,
      uwagi:      itemForm.uwagi || null,
    };
    try {
      if (itemModal.mode === 'add') {
        await api.post('/task-items', payload);
        await refresh();
        setItemForm({ kod_modelu: payload.kod_modelu, ilosc: String(payload.ilosc), uwagi: '' });
        setTimeout(() => { if (kodModeluRef.current) kodModeluRef.current.focus(); }, 50);
      } else {
        await api.put(`/task-items/${itemModal.item.id}`, payload);
        await refresh();
        closeItemModal();
      }
    } catch (err) {
      setItemError(err.response?.data?.error || 'Błąd zapisu pozycji');
    } finally { setSavingItem(false); }
  };

  const deleteItem = async (id) => {
    try { await api.delete(`/task-items/${id}`); setDeleteConfirmItem(null); await refresh(); } catch {}
  };

  /* ── Render ── */
  const isFile = (name, ext) => name?.toLowerCase().endsWith(ext);
  const isExcel = (name) => isFile(name, '.xls') || isFile(name, '.xlsx');

  return (
    <>
      <style>{`@keyframes drawer-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
      <div style={ds.overlay} onClick={onClose} />
      <div style={ds.drawer}>

        {/* Nagłówek */}
        <div style={ds.drawerHeader}>
          <div style={{ minWidth: 0 }}>
            <div style={ds.drawerTitle}>{task?.nazwa || '...'}</div>
            {task?.fabryka && <div style={ds.drawerSubtitle}>🏭 {task.fabryka.nazwa}</div>}
          </div>
          <div style={ds.drawerHeaderBtns}>
            {canEdit && task && <button style={ds.btnEdit} onClick={openEdit}>Edytuj</button>}
            {isAdmin && task && <button style={ds.btnDanger} onClick={() => setDeleteConfirmTask(true)}>Usuń</button>}
            <button style={ds.btnClose} onClick={onClose}>✕</button>
          </div>
        </div>

        <div style={ds.drawerBody}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#8b949e' }}>Ładowanie...</div>
          ) : error ? (
            <div style={ds.errorBanner}>{error}</div>
          ) : (
            <>
              {/* Status */}
              <div style={ds.section}>
                <h4 style={ds.sectionTitle}>Status</h4>
                <StatusDropdown value={task.status} isAdmin={isAdmin || task.assigned_to === user.id} onChange={handleStatusChange} />
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#8b949e' }}>
                  Autor: {task.autor?.imie || '—'} · Utworzone: {fmt(task.created_at)}
                </div>
                <div style={{ marginTop: '4px', fontSize: '13px', color: task.przydzielony ? '#58a6ff' : '#484f58' }}>
                  Przydzielono: {task.przydzielony?.imie || '— nieprzydzielone —'}
                </div>
              </div>

              {/* Pozycje */}
              <div style={ds.section}>
                <div style={ds.sectionRow}>
                  <h4 style={ds.sectionTitle}>Pozycje</h4>
                  {canEdit && <button style={ds.btnSmallBlue} onClick={openAddItem}>+ Dodaj pozycję</button>}
                </div>
                {task.items?.length > 0 ? (
                  <div style={ds.tableWrap}>
                    <table style={ds.table}>
                      <thead>
                        <tr>
                          <th style={{ ...ds.th, width: 32 }}></th>
                          <th style={ds.th}>Kod modelu</th>
                          <th style={ds.thR}>Ilość</th>
                          <th style={ds.th}>Uwagi</th>
                          {canEdit && <th style={ds.th}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {task.items.map((it, i) => (
                          <tr key={it.id} style={i % 2 === 0 ? ds.trEven : ds.trOdd}>
                            <td style={{ ...ds.td, textAlign: 'center' }}>
                              <button
                                onClick={() => handleToggleItemDrawer(it)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 2px' }}
                                title={it.zrobione ? 'Oznacz jako niezrobione' : 'Oznacz jako zrobione'}
                              >
                                {it.zrobione ? '✅' : '❌'}
                              </button>
                            </td>
                            <td style={{ ...ds.td, fontWeight: '600', color: it.zrobione ? '#8b949e' : '#e6edf3', textDecoration: it.zrobione ? 'line-through' : 'none' }}>{it.kod_modelu}</td>
                            <td style={ds.tdR}>{it.ilosc}</td>
                            <td style={{ ...ds.td, color: '#8b949e' }}>{it.uwagi || '—'}</td>
                            {canEdit && (
                              <td style={ds.td}>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button style={ds.btnIcon} onClick={() => openEditItem(it)}>✎</button>
                                  <button style={ds.btnIconDel} onClick={() => setDeleteConfirmItem(it)}>✕</button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p style={ds.emptyText}>Brak pozycji</p>}
              </div>

              {/* Notatki i załączniki */}
              <div style={ds.section}>
                <h4 style={ds.sectionTitle}>Notatki i załączniki</h4>
                {(() => {
                  const feed = [
                    ...(task.notes  || []).map(n => ({ ...n, _type: 'note' })),
                    ...(task.photos || []).map(p => ({ ...p, _type: 'photo' })),
                  ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                  if (feed.length === 0) return <p style={ds.emptyText}>Brak notatek i załączników</p>;
                  return (
                    <div style={ds.notesList}>
                      {feed.map(item => !item.plik ? (
                        <div key={`note-${item.id}`} style={ds.noteItem}>
                          <div style={ds.noteMeta}>
                            <span style={ds.noteAuthor}>{item.autor?.imie || '—'}</span>
                            <span style={ds.dot}>·</span>
                            <span style={ds.metaDate}>{fmt(item.created_at)}</span>
                            {isAdmin && (
                              <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                                <button style={ds.btnIcon} onClick={() => startEditNote(item)}>✎</button>
                                <button style={ds.btnIconDel} onClick={() => deleteNote(item.id)}>✕</button>
                              </div>
                            )}
                          </div>
                          {editNoteId === item.id ? (
                            <>
                              <textarea style={{ ...ds.textarea, marginBottom: '6px' }} value={editNoteText} onChange={e => setEditNoteText(e.target.value)} rows={3} autoFocus />
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                <button style={ds.btnCancel} onClick={() => setEditNoteId(null)}>Anuluj</button>
                                <button style={ds.btnSave} onClick={() => saveEditNote(item.id)}>Zapisz</button>
                              </div>
                            </>
                          ) : <p style={ds.noteText}>{item.tresc}</p>}
                        </div>
                      ) : (
                        <div key={`photo-${item.id}`} style={ds.noteItem}>
                          <div style={ds.noteMeta}>
                            <span style={ds.noteAuthor}>{item.autor?.imie || '—'}</span>
                            <span style={ds.dot}>·</span>
                            <span style={ds.metaDate}>{fmt(item.created_at)}</span>
                            <span style={ds.dot}>·</span>
                            <span style={{ ...ds.metaDate, color: '#79c0ff' }}>
                              {isFile(item.plik, '.pdf') ? 'PDF' : isExcel(item.plik) ? 'Excel' : 'zdjęcie'}
                            </span>
                            {isAdmin && (
                              <div style={{ marginLeft: 'auto' }}>
                                <button style={ds.btnIconDel} onClick={() => deletePhoto(item.id)}>✕</button>
                              </div>
                            )}
                          </div>
                          {isFile(item.plik, '.pdf') ? (
                            <a href={fileUrl(item.plik)} target="_blank" rel="noopener noreferrer" style={ds.pdfLink}>📄 {item.plik}</a>
                          ) : isExcel(item.plik) ? (
                            <a href={fileUrl(item.plik)} target="_blank" rel="noopener noreferrer" style={ds.pdfLink}>📊 {item.plik}</a>
                          ) : (
                            <div style={ds.attachThumb} onClick={() => setLightbox(item)}>
                              <img src={fileUrl(item.plik)} alt={item.opis || 'załącznik'} style={ds.photoImg} />
                            </div>
                          )}
                          {item.opis && <p style={ds.noteText}>{item.opis}</p>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {isAdmin && (
                  <form onSubmit={addNoteOrPhoto} style={ds.addForm}>
                    <textarea
                      style={ds.textarea}
                      placeholder="Dodaj notatkę..."
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      onPaste={e => handleAttachmentPaste(e, handleAttachFile)}
                      rows={2}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                      <input ref={fileInputRef} type="file" accept={ATTACHMENT_ACCEPT} style={{ display: 'none' }} onChange={e => handleAttachFile(e.target.files[0])} />
                      <button type="button" style={ds.btnAttach} onClick={() => fileInputRef.current?.click()}>📎</button>
                      {attachFile && (
                        <span style={{ fontSize: '12px', color: '#8b949e', flex: 1 }}>
                          {attachPreview ? <img src={attachPreview} alt="" style={{ height: '32px', borderRadius: '4px' }} /> : attachFile.name}
                          <button type="button" style={{ ...ds.btnIconDel, marginLeft: '6px' }} onClick={clearAttach}>✕</button>
                        </span>
                      )}
                      <button type="submit" style={ds.btnSave} disabled={savingNote}>
                        {savingNote ? '...' : 'Dodaj'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Komentarze */}
              <div style={ds.section}>
                <h4 style={ds.sectionTitle}>Komentarze</h4>
                {(task.comments || []).length === 0 && <p style={ds.emptyText}>Brak komentarzy</p>}
                <div style={ds.notesList}>
                  {(task.comments || []).map(c => (
                    <div key={c.id} style={ds.noteItem}>
                      <div style={ds.noteMeta}>
                        <span style={ds.noteAuthor}>{c.autor?.imie || '—'}</span>
                        <span style={ds.dot}>·</span>
                        <span style={ds.metaDate}>{fmt(c.created_at)}</span>
                        {(isAdmin || c.created_by === user.id) && (
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                            <button style={ds.btnIcon} onClick={() => startEditComment(c)}>✎</button>
                            <button style={ds.btnIconDel} onClick={() => deleteComment(c.id)}>✕</button>
                          </div>
                        )}
                      </div>
                      {editCommentId === c.id ? (
                        <>
                          <textarea style={{ ...ds.textarea, marginBottom: '6px' }} value={editCommentText} onChange={e => setEditCommentText(e.target.value)} rows={3} autoFocus />
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button style={ds.btnCancel} onClick={() => setEditCommentId(null)}>Anuluj</button>
                            <button style={ds.btnSave} onClick={() => saveEditComment(c.id)}>Zapisz</button>
                          </div>
                        </>
                      ) : (
                        <>
                          {c.tresc && <p style={ds.commentText}>{c.tresc}</p>}
                          {c.zdjecie && (
                            isFile(c.zdjecie, '.pdf') ? (
                              <a href={fileUrl(c.zdjecie)} target="_blank" rel="noopener noreferrer" style={ds.pdfLink}>📄 {c.zdjecie}</a>
                            ) : isExcel(c.zdjecie) ? (
                              <a href={fileUrl(c.zdjecie)} target="_blank" rel="noopener noreferrer" style={ds.pdfLink}>📊 {c.zdjecie}</a>
                            ) : (
                              <div style={ds.attachThumb} onClick={() => setLightbox({ plik: c.zdjecie })}>
                                <img src={fileUrl(c.zdjecie)} alt="" style={ds.photoImg} />
                              </div>
                            )
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <form onSubmit={addComment} style={ds.addForm}>
                  <textarea
                    style={ds.textarea}
                    placeholder="Napisz komentarz..."
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onPaste={e => handleAttachmentPaste(e, handleCommentFile)}
                    rows={2}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                    <input ref={commentFileRef} type="file" accept={ATTACHMENT_ACCEPT} style={{ display: 'none' }} onChange={e => handleCommentFile(e.target.files[0])} />
                    <button type="button" style={ds.btnAttach} onClick={() => commentFileRef.current?.click()}>📎</button>
                    {commentFile && (
                      <span style={{ fontSize: '12px', color: '#8b949e', flex: 1 }}>
                        {commentPreview ? <img src={commentPreview} alt="" style={{ height: '32px', borderRadius: '4px' }} /> : commentFile.name}
                        <button type="button" style={{ ...ds.btnIconDel, marginLeft: '6px' }} onClick={clearCommentFile}>✕</button>
                      </span>
                    )}
                    <button type="submit" style={ds.btnSave} disabled={savingComment}>
                      {savingComment ? '...' : 'Wyślij'}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal edycji zadania */}
      {editOpen && (
        <div style={ds.modalOverlay} onClick={() => setEditOpen(false)}>
          <div style={ds.modal} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <h3 style={ds.modalTitle}>Edytuj zadanie</h3>
              <button style={ds.btnClose} onClick={() => setEditOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Nazwa zadania</label>
                <input style={ds.mInput} value={editForm.nazwa} onChange={e => setEditForm({ ...editForm, nazwa: e.target.value })} required />
              </div>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Fabryka (opcjonalnie)</label>
                <select style={ds.mInput} value={editForm.fabryka_id} onChange={e => setEditForm({ ...editForm, fabryka_id: e.target.value })}>
                  <option value="">— brak —</option>
                  {factories.map(f => <option key={f.id} value={f.id}>{f.nazwa}</option>)}
                </select>
              </div>
              {isAdmin && (
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Przydzielono do (opcjonalnie)</label>
                  <select style={ds.mInput} value={editForm.assigned_to} onChange={e => setEditForm({ ...editForm, assigned_to: e.target.value })}>
                    <option value="">— nieprzydzielone —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.imie} ({u.rola})</option>)}
                  </select>
                </div>
              )}
              {editError && <p style={ds.mError}>{editError}</p>}
              <div style={ds.modalFooter}>
                <button type="button" style={ds.btnCancel} onClick={() => setEditOpen(false)}>Anuluj</button>
                <button type="submit" style={ds.btnSave} disabled={savingEdit}>{savingEdit ? 'Zapisywanie...' : 'Zapisz'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal dodawania/edycji pozycji */}
      {itemModal && (
        <div style={ds.modalOverlay} onClick={closeItemModal}>
          <div style={ds.modal} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <h3 style={ds.modalTitle}>{itemModal.mode === 'add' ? 'Dodaj pozycję' : 'Edytuj pozycję'}</h3>
              <button style={ds.btnClose} onClick={closeItemModal}>✕</button>
            </div>
            <form onSubmit={handleItemSubmit}>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Kod modelu</label>
                <input ref={kodModeluRef} style={ds.mInput} value={itemForm.kod_modelu} onChange={e => setItemForm({ ...itemForm, kod_modelu: e.target.value })} required autoFocus />
              </div>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Ilość</label>
                <input style={ds.mInput} type="number" min="1" value={itemForm.ilosc} onChange={e => setItemForm({ ...itemForm, ilosc: e.target.value })} />
              </div>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Uwagi</label>
                <input style={ds.mInput} value={itemForm.uwagi} onChange={e => setItemForm({ ...itemForm, uwagi: e.target.value })} placeholder="opcjonalnie" />
              </div>
              {itemError && <p style={ds.mError}>{itemError}</p>}
              <div style={ds.modalFooter}>
                <button type="button" style={ds.btnCancel} onClick={closeItemModal}>Anuluj</button>
                <button type="submit" style={ds.btnSave} disabled={savingItem}>{savingItem ? 'Zapisywanie...' : 'Zapisz'}</button>
                {itemModal.mode === 'add' && <span style={{ fontSize: '12px', color: '#8b949e' }}>Enter = zapisz i dodaj kolejną</span>}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Potwierdzenie usunięcia pozycji */}
      {deleteConfirmItem && (
        <div style={ds.modalOverlay} onClick={() => setDeleteConfirmItem(null)}>
          <div style={{ ...ds.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <h3 style={ds.modalTitle}>Usuń pozycję</h3>
              <button style={ds.btnClose} onClick={() => setDeleteConfirmItem(null)}>✕</button>
            </div>
            <p style={ds.confirmText}>Czy na pewno chcesz usunąć <strong style={{ color: '#e6edf3' }}>{deleteConfirmItem.kod_modelu}</strong>?</p>
            <div style={ds.modalFooter}>
              <button style={ds.btnCancel} onClick={() => setDeleteConfirmItem(null)}>Anuluj</button>
              <button style={ds.btnDanger} onClick={() => deleteItem(deleteConfirmItem.id)}>Usuń</button>
            </div>
          </div>
        </div>
      )}

      {/* Potwierdzenie usunięcia zadania */}
      {deleteConfirmTask && (
        <div style={ds.modalOverlay} onClick={() => setDeleteConfirmTask(false)}>
          <div style={{ ...ds.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <h3 style={ds.modalTitle}>Usuń zadanie</h3>
              <button style={ds.btnClose} onClick={() => setDeleteConfirmTask(false)}>✕</button>
            </div>
            <p style={ds.confirmText}>Czy na pewno chcesz usunąć zadanie <strong style={{ color: '#e6edf3' }}>{task?.nazwa}</strong>? Tej operacji nie można cofnąć.</p>
            <div style={ds.modalFooter}>
              <button style={ds.btnCancel} onClick={() => setDeleteConfirmTask(false)}>Anuluj</button>
              <button style={ds.btnDanger} onClick={handleDeleteTask}>Usuń</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: wszystkie pozycje zrobione → zmień status zadania */}
      {confirmAllDoneDrawer && (
        <div style={ds.modalOverlay} onClick={() => setConfirmAllDoneDrawer(false)}>
          <div style={{ ...ds.modal, maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <span style={ds.modalTitle}>Wszystkie pozycje zrobione</span>
              <button style={ds.btnClose} onClick={() => setConfirmAllDoneDrawer(false)}>✕</button>
            </div>
            <p style={{ padding: '16px 20px', color: '#c9d1d9', fontSize: '14px', margin: 0 }}>
              Wszystkie pozycje zostały zaznaczone jako zrobione. Czy zmienić status całego zadania na <strong style={{ color: '#3fb950' }}>ZROBIONE</strong>?
            </p>
            <div style={ds.modalFooter}>
              <button style={ds.btnCancel} onClick={() => setConfirmAllDoneDrawer(false)}>Nie</button>
              <button style={ds.btnSave} onClick={handleConfirmTaskDoneDrawer}>Tak, zmień status</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div style={ds.lightboxOverlay} onClick={() => setLightbox(null)}>
          <div style={ds.lightboxBox} onClick={e => e.stopPropagation()}>
            <img src={fileUrl(lightbox.plik)} alt={lightbox.opis || ''} style={ds.lightboxImg} />
            <button style={ds.lightboxClose} onClick={() => setLightbox(null)}>✕</button>
          </div>
        </div>
      )}
    </>
  );
}

/* ── TaskRow ─────────────────────────────────────────────────────────────── */

function TaskRow({ task: t, index: i, isAdmin, user, onSelect, onStatusChange, onAllItemsDone, onItemToggled }) {
  const [hovered, setHovered]   = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [items, setItems]       = useState(t.items || []);
  const base = i % 2 === 0 ? '#161b22' : '#0d1117';
  const colSpan = 6;
  const canChangeStatus = isAdmin || t.assigned_to === user.id;

  useEffect(() => { setItems(t.items || []); }, [t.items]);

  const handleToggleItem = async (e, item) => {
    e.stopPropagation();
    const newVal = !item.zrobione;
    try {
      await api.put(`/task-items/${item.id}`, { zrobione: newVal });
      const updated = items.map(it => it.id === item.id ? { ...it, zrobione: newVal } : it);
      setItems(updated);
      onItemToggled && onItemToggled();
      if (newVal && updated.every(it => it.zrobione) && canChangeStatus) {
        onAllItemsDone(t.id);
      }
    } catch {}
  };

  return (
    <>
      <tr
        style={{ background: hovered ? '#1c2128' : base, cursor: 'pointer', transition: 'background 0.1s' }}
        onClick={() => onSelect(t.id)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <td style={{ ...s.td, width: '32px', padding: '9px 8px 9px 12px' }}>
          {t.items?.length > 0 && (
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b949e', fontSize: '11px', padding: '2px 4px', lineHeight: 1, borderRadius: '3px' }}
              onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
              title={expanded ? 'Zwiń' : 'Rozwiń pozycje'}
            >
              {expanded ? '▼' : '▶'}
            </button>
          )}
        </td>
        <td style={{ ...s.td, fontWeight: '600', color: '#e6edf3' }}>{t.nazwa}</td>
        <td style={s.td}>{t.fabryka?.nazwa || <span style={s.dash}>—</span>}</td>
        <td style={s.td}>
          <StatusDropdown value={t.status} isAdmin={canChangeStatus} onChange={v => onStatusChange(t.id, v)} />
        </td>
        <td style={{ ...s.td, color: '#8b949e' }}>{t.autor?.imie || '—'}</td>
        <td style={{ ...s.td, color: t.przydzielony ? '#58a6ff' : '#484f58' }}>{t.przydzielony?.imie || '—'}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={colSpan} style={{ padding: 0, background: '#0d1117', borderBottom: '2px solid #30363d' }}>
            <div style={{ animation: 'tr-expand 0.15s ease', padding: '10px 16px 12px 44px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #21262d' }}>
                    <th style={{ ...s.expandTh, width: 28 }}></th>
                    <th style={s.expandTh}>Kod modelu</th>
                    <th style={{ ...s.expandTh, textAlign: 'right' }}>Ilość</th>
                    <th style={s.expandTh}>Uwagi</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => {
                    const cell = { padding: '5px 10px', fontSize: '12px', color: '#c9d1d9', borderBottom: '1px solid #161b22' };
                    return (
                      <tr key={it.id}>
                        <td style={{ ...cell, textAlign: 'center' }}>
                          <button
                            onClick={e => handleToggleItem(e, it)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 2px' }}
                            title={it.zrobione ? 'Oznacz jako niezrobione' : 'Oznacz jako zrobione'}
                          >
                            {it.zrobione ? '✅' : '❌'}
                          </button>
                        </td>
                        <td style={{ ...cell, fontWeight: '500', textDecoration: it.zrobione ? 'line-through' : 'none', color: it.zrobione ? '#8b949e' : '#c9d1d9' }}>{it.kod_modelu}</td>
                        <td style={{ ...cell, textAlign: 'right' }}>{it.ilosc ?? '—'}</td>
                        <td style={{ ...cell, color: '#8b949e' }}>{it.uwagi || <span style={{ fontStyle: 'italic' }}>—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Główny komponent Tasks ──────────────────────────────────────────────── */

export default function Tasks({ user, onTasksChanged }) {
  const isAdmin = user.rola === 'ADMIN';
  const [tasks, setTasks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({ nazwa: '', fabryka_id: '', assigned_to: '' });
  const [savingNew, setSavingNew] = useState(false);
  const [newTaskError, setNewTaskError] = useState('');
  const [factories, setFactories] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [hideZrobione, setHideZrobione] = useState(true);

  const load = async () => {
    try {
      const res = await api.get('/tasks');
      setTasks(res.data);
      onTasksChanged && onTasksChanged();
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNewTask = () => {
    setNewTaskForm({ nazwa: '', fabryka_id: '', assigned_to: '' });
    setNewTaskError('');
    if (factories.length === 0) api.get('/factories').then(r => setFactories(r.data)).catch(() => {});
    if (users.length === 0) api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    setNewTaskOpen(true);
  };

  const handleNewTaskSubmit = async (e) => {
    e.preventDefault();
    setSavingNew(true);
    try {
      await api.post('/tasks', { nazwa: newTaskForm.nazwa, fabryka_id: newTaskForm.fabryka_id || null, assigned_to: newTaskForm.assigned_to || null });
      setNewTaskOpen(false);
      await load();
    } catch (err) {
      setNewTaskError(err.response?.data?.error || 'Błąd zapisu');
    } finally { setSavingNew(false); }
  };

  const handleStatusChange = async (taskId, status) => {
    await api.put(`/tasks/${taskId}`, { status });
    await load();
  };

  const [confirmAllDoneId, setConfirmAllDoneId] = useState(null);

  const handleAllItemsDone = (taskId) => setConfirmAllDoneId(taskId);

  const handleConfirmTaskDone = async () => {
    await handleStatusChange(confirmAllDoneId, 'ZROBIONE');
    setConfirmAllDoneId(null);
  };

  const filtered = tasks.filter(t => {
    if (hideZrobione && t.status === 'ZROBIONE') return false;
    if (filterStatus && t.status !== filterStatus) return false;
    if (searchQuery && !t.nazwa.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={s.page}>
      {/* Nagłówek */}
      <div style={s.pageHeader}>
        <div>
          <h1 style={s.pageTitle}>Do zrobienia</h1>
          <p style={s.pageCount}>{loading ? '...' : `${filtered.length} zadań`}</p>
        </div>
        <button style={s.btnNew} onClick={openNewTask}>+ Nowe zadanie</button>
      </div>

      {/* Filtry */}
      <div style={s.filterBar}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            style={s.searchInput}
            placeholder="Szukaj zadania…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <select style={s.sortSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Wszystkie statusy</option>
            <option value="DO_ZROBIENIA">DO ZROBIENIA</option>
            <option value="W_TRAKCIE">W TRAKCIE</option>
            <option value="ZROBIONE">ZROBIONE</option>
          </select>
          <label style={s.checkLabel}>
            <input type="checkbox" checked={hideZrobione} onChange={e => setHideZrobione(e.target.checked)} style={{ marginRight: 6 }} />
            Ukryj zakończone
          </label>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={s.loadingWrap}><p style={{ color: '#8b949e' }}>Ładowanie...</p></div>
      ) : filtered.length === 0 ? (
        <div style={s.loadingWrap}><p style={{ color: '#8b949e' }}>Brak zadań</p></div>
      ) : (
        <div style={s.tableWrap}>
          <style>{`@keyframes tr-expand { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '32px', padding: '9px 8px' }}></th>
                <th style={s.th}>Zadanie</th>
                <th style={s.th}>Fabryka</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Autor</th>
                <th style={s.th}>Przydzielono</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <TaskRow key={t.id} task={t} index={i} isAdmin={isAdmin} user={user} onSelect={setSelectedId} onStatusChange={handleStatusChange} onAllItemsDone={handleAllItemsDone} onItemToggled={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer */}
      {selectedId && (
        <TaskDrawer
          taskId={selectedId}
          isAdmin={isAdmin}
          user={user}
          onClose={() => setSelectedId(null)}
          onUpdated={(t) => { if (!t) setSelectedId(null); load(); }}
        />
      )}

      {/* Modal: wszystkie pozycje zrobione → zmień status zadania */}
      {confirmAllDoneId && (
        <div style={ds.modalOverlay} onClick={() => setConfirmAllDoneId(null)}>
          <div style={{ ...ds.modal, maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <span style={ds.modalTitle}>Wszystkie pozycje zrobione</span>
              <button style={ds.btnClose} onClick={() => setConfirmAllDoneId(null)}>✕</button>
            </div>
            <p style={{ padding: '16px 20px', color: '#c9d1d9', fontSize: '14px', margin: 0 }}>
              Wszystkie pozycje zostały zaznaczone jako zrobione. Czy zmienić status całego zadania na <strong style={{ color: '#3fb950' }}>ZROBIONE</strong>?
            </p>
            <div style={ds.modalFooter}>
              <button style={ds.btnCancel} onClick={() => setConfirmAllDoneId(null)}>Nie</button>
              <button style={ds.btnSave} onClick={handleConfirmTaskDone}>Tak, zmień status</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nowego zadania */}
      {newTaskOpen && (
        <div style={ds.modalOverlay} onClick={() => setNewTaskOpen(false)}>
          <div style={ds.modal} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <h3 style={ds.modalTitle}>Nowe zadanie</h3>
              <button style={ds.btnClose} onClick={() => setNewTaskOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleNewTaskSubmit}>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Nazwa zadania</label>
                <input style={ds.mInput} value={newTaskForm.nazwa} onChange={e => setNewTaskForm({ ...newTaskForm, nazwa: e.target.value })} required autoFocus />
              </div>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Fabryka (opcjonalnie)</label>
                <select style={ds.mInput} value={newTaskForm.fabryka_id} onChange={e => setNewTaskForm({ ...newTaskForm, fabryka_id: e.target.value })}>
                  <option value="">— brak —</option>
                  {factories.map(f => <option key={f.id} value={f.id}>{f.nazwa}</option>)}
                </select>
              </div>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Przydzielono do (opcjonalnie)</label>
                <select style={ds.mInput} value={newTaskForm.assigned_to} onChange={e => setNewTaskForm({ ...newTaskForm, assigned_to: e.target.value })}>
                  <option value="">— nieprzydzielone —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.imie} ({u.rola})</option>)}
                </select>
              </div>
              {newTaskError && <p style={ds.mError}>{newTaskError}</p>}
              <div style={ds.modalFooter}>
                <button type="button" style={ds.btnCancel} onClick={() => setNewTaskOpen(false)}>Anuluj</button>
                <button type="submit" style={ds.btnSave} disabled={savingNew}>{savingNew ? 'Zapisywanie...' : 'Utwórz'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Style ───────────────────────────────────────────────────────────────── */

const s = {
  page:        { padding: '24px', maxWidth: '1200px', margin: '0 auto' },
  pageHeader:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' },
  pageTitle:   { margin: 0, fontSize: '24px', fontWeight: '700', color: '#e6edf3' },
  pageCount:   { margin: '4px 0 0', fontSize: '13px', color: '#8b949e' },
  btnNew:      { padding: '8px 18px', background: '#238636', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  filterBar:   { display: 'flex', alignItems: 'center', background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', gap: '8px', flexWrap: 'wrap' },
  searchInput: { background: '#0d1117', border: '1px solid #30363d', borderRadius: '6px', color: '#c9d1d9', padding: '6px 12px', fontSize: '13px', outline: 'none', width: '220px' },
  sortSelect:  { background: '#0d1117', border: '1px solid #30363d', borderRadius: '6px', color: '#c9d1d9', padding: '6px 10px', fontSize: '13px', cursor: 'pointer' },
  checkLabel:  { display: 'flex', alignItems: 'center', fontSize: '13px', color: '#8b949e', cursor: 'pointer', userSelect: 'none' },
  tableWrap:   { overflowX: 'auto', borderRadius: '8px', border: '1px solid #30363d' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:          { padding: '10px 16px', textAlign: 'left', color: '#8b949e', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#161b22', borderBottom: '1px solid #30363d', whiteSpace: 'nowrap' },
  td:          { padding: '12px 16px', borderBottom: '1px solid #21262d', color: '#c9d1d9', verticalAlign: 'middle' },
  dash:        { color: '#484f58' },
  loadingWrap: { display: 'flex', justifyContent: 'center', padding: '60px' },
  expandTh:    { padding: '5px 10px', textAlign: 'left', color: '#8b949e', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' },
};

const ds = {
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300 },
  drawer:       { position: 'fixed', top: 0, right: 0, width: '560px', maxWidth: '100vw', height: '100vh', background: '#161b22', borderLeft: '1px solid #30363d', zIndex: 301, display: 'flex', flexDirection: 'column', animation: 'drawer-slide-in 0.2s ease' },
  drawerHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #30363d', flexShrink: 0 },
  drawerTitle:  { fontSize: '18px', fontWeight: '700', color: '#e6edf3', margin: 0 },
  drawerSubtitle: { fontSize: '13px', color: '#8b949e', marginTop: '4px' },
  drawerHeaderBtns: { display: 'flex', gap: '8px', flexShrink: 0 },
  drawerBody:   { flex: 1, overflowY: 'auto', padding: '0 24px 24px' },
  section:      { borderBottom: '1px solid #21262d', paddingBottom: '20px', marginBottom: '20px', paddingTop: '20px' },
  sectionTitle: { margin: '0 0 12px', fontSize: '13px', fontWeight: '600', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.05em' },
  sectionRow:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  emptyText:    { color: '#484f58', fontSize: '13px', fontStyle: 'italic', margin: '8px 0' },
  errorBanner:  { background: '#3d0c0c', border: '1px solid #6b1515', borderRadius: '6px', padding: '12px 16px', color: '#f85149', fontSize: '13px', margin: '20px 0' },
  notesList:    { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' },
  noteItem:     { background: '#0d1117', borderRadius: '6px', padding: '10px 14px', border: '1px solid #21262d' },
  noteMeta:     { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' },
  noteAuthor:   { fontWeight: '600', fontSize: '12px', color: '#58a6ff' },
  dot:          { color: '#484f58', fontSize: '12px' },
  metaDate:     { fontSize: '12px', color: '#8b949e' },
  noteText:     { margin: 0, fontSize: '13px', color: '#c9d1d9', lineHeight: '1.5', whiteSpace: 'pre-wrap' },
  commentText:  { margin: '0 0 6px', fontSize: '13px', color: '#c9d1d9', lineHeight: '1.5', whiteSpace: 'pre-wrap' },
  addForm:      { marginTop: '12px' },
  textarea:     { width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: '6px', color: '#c9d1d9', padding: '8px 12px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box' },
  btnSmallBlue: { padding: '4px 12px', background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' },
  btnAttach:    { background: 'none', border: '1px solid #30363d', borderRadius: '6px', color: '#8b949e', fontSize: '16px', cursor: 'pointer', padding: '4px 8px' },
  btnEdit:      { padding: '7px 16px', background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  btnClose:     { padding: '7px 13px', background: 'none', border: '1px solid #30363d', color: '#8b949e', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', lineHeight: 1 },
  btnSave:      { padding: '7px 16px', background: '#238636', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' },
  btnCancel:    { padding: '7px 16px', background: 'none', color: '#8b949e', border: '1px solid #30363d', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' },
  btnDanger:    { padding: '7px 16px', background: '#da3633', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' },
  btnIcon:      { background: 'none', border: 'none', color: '#58a6ff', fontSize: '14px', cursor: 'pointer', padding: '2px 4px' },
  btnIconDel:   { background: 'none', border: 'none', color: '#f85149', fontSize: '14px', cursor: 'pointer', padding: '2px 4px' },
  tableWrap:    { overflowX: 'auto', borderRadius: '6px', border: '1px solid #30363d' },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:           { padding: '8px 12px', textAlign: 'left', color: '#8b949e', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', background: '#161b22', borderBottom: '1px solid #30363d' },
  thR:          { padding: '8px 12px', textAlign: 'right', color: '#8b949e', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', background: '#161b22', borderBottom: '1px solid #30363d' },
  td:           { padding: '8px 12px', borderBottom: '1px solid #21262d', color: '#c9d1d9', verticalAlign: 'middle' },
  tdR:          { padding: '8px 12px', borderBottom: '1px solid #21262d', color: '#c9d1d9', verticalAlign: 'middle', textAlign: 'right' },
  trEven:       { background: '#161b22' },
  trOdd:        { background: '#0d1117' },
  pdfLink:      { color: '#58a6ff', fontSize: '13px', textDecoration: 'none', display: 'inline-block', marginTop: '4px' },
  attachThumb:  { display: 'inline-block', marginBottom: '4px', cursor: 'pointer' },
  photoImg:     { maxWidth: '120px', height: 'auto', display: 'block', borderRadius: '6px', border: '1px solid #30363d', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:        { background: '#161b22', border: '1px solid #30363d', borderRadius: '10px', width: '480px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #30363d' },
  modalTitle:   { margin: 0, fontSize: '16px', fontWeight: '700', color: '#e6edf3' },
  modalFooter:  { display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid #30363d' },
  mField:       { padding: '12px 20px 0' },
  mLabel:       { display: 'block', fontSize: '12px', fontWeight: '600', color: '#8b949e', marginBottom: '6px', textTransform: 'uppercase' },
  mInput:       { width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: '6px', color: '#c9d1d9', padding: '8px 12px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  mError:       { margin: '8px 20px 0', color: '#f85149', fontSize: '13px' },
  confirmText:  { padding: '16px 20px', margin: 0, color: '#c9d1d9', fontSize: '14px' },
  spinner:      { width: '32px', height: '32px', border: '3px solid #30363d', borderTopColor: '#58a6ff', borderRadius: '50%', animation: 'ds-spin 0.8s linear infinite', display: 'inline-block' },
  lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  lightboxBox:  { position: 'relative', maxWidth: '90vw', maxHeight: '90vh' },
  lightboxImg:  { maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '6px' },
  lightboxClose: { position: 'absolute', top: '-16px', right: '-16px', background: '#21262d', border: '1px solid #30363d', color: '#c9d1d9', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px' },
};
